import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, parseError } from "../../../utils";
import ToolsetFormDialog from "./ToolsetFormDialog";

interface ToolsetIntegration {
  id: string;
  name: string;
  instanceName: string;
  type: string;
  parameters?: {
    description?: string;
    tools?: Record<string, { integrationName: string; integrationType: string; tools: string[] }>;
  };
}

interface ToolsetsManagementDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
}

const ToolsetsManagementDialog: React.FC<ToolsetsManagementDialogProps> = ({
  open,
  onClose,
  companyId,
  getToken,
  eventListener,
}) => {
  const [loading, setLoading] = useState(false);
  const [toolsets, setToolsets] = useState<ToolsetIntegration[]>([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedToolset, setSelectedToolset] = useState<ToolsetIntegration | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmToolset, setDeleteConfirmToolset] = useState<ToolsetIntegration | null>(null);

  const fetchToolsets = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/integrations/by-type/toolset`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setToolsets(response.data.items || []);
    } catch (error) {
      console.error("Error fetching toolsets:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to load toolsets",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchToolsets();
    }
  }, [open, companyId]);

  const handleCreate = () => {
    setSelectedToolset(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (toolset: ToolsetIntegration) => {
    setSelectedToolset(toolset);
    setFormDialogOpen(true);
  };

  const handleDelete = async (toolset: ToolsetIntegration) => {
    setDeletingId(toolset.id);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/integrations/remove`,
        { integrationId: toolset.id, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      eventListener({
        type: "SET_SUCCESS",
        message: "Toolset deleted successfully",
      });
      fetchToolsets();
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setDeletingId(null);
      setDeleteConfirmToolset(null);
    }
  };

  const handleFormSuccess = () => {
    setFormDialogOpen(false);
    fetchToolsets();
  };

  const getToolCount = (toolset: ToolsetIntegration): number => {
    // Use pre-computed count from by-type endpoint, or calculate from tools if available
    if ((toolset.parameters as any)?.toolCount !== undefined) {
      return (toolset.parameters as any).toolCount;
    }
    if (!toolset.parameters?.tools) return 0;
    return Object.values(toolset.parameters.tools).reduce(
      (sum, integration) => sum + (integration.tools?.length || 0),
      0
    );
  };

  const getIntegrationCount = (toolset: ToolsetIntegration): number => {
    // Use pre-computed count from by-type endpoint, or calculate from tools if available
    if ((toolset.parameters as any)?.integrationCount !== undefined) {
      return (toolset.parameters as any).integrationCount;
    }
    if (!toolset.parameters?.tools) return 0;
    return Object.keys(toolset.parameters.tools).length;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage Toolsets</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Toolsets are curated collections of specific tools from your connected integrations.
              Create focused toolsets for different use cases and assign them to your AI employees.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleCreate}
              fullWidth
            >
              Create Toolset
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : toolsets.length === 0 ? (
            <Box
              sx={{
                p: 3,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No toolsets created yet. Create your first toolset to curate specific tools for
                your AI employees.
              </Typography>
            </Box>
          ) : (
            <List>
              {toolsets.map((toolset) => (
                <ListItem
                  key={toolset.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle2">{toolset.instanceName}</Typography>
                        <Chip
                          label={`${getToolCount(toolset)} tools`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${getIntegrationCount(toolset)} integrations`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      toolset.parameters?.description || (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          No description
                        </Typography>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Edit">
                      <IconButton
                        edge="end"
                        onClick={() => handleEdit(toolset)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        edge="end"
                        onClick={() => setDeleteConfirmToolset(toolset)}
                        size="small"
                        disabled={deletingId === toolset.id}
                      >
                        {deletingId === toolset.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <ToolsetFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSuccess={handleFormSuccess}
        toolset={selectedToolset}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmToolset !== null}
        onClose={() => setDeleteConfirmToolset(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Toolset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirmToolset?.instanceName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmToolset(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmToolset && handleDelete(deleteConfirmToolset)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ToolsetsManagementDialog;
